import { ClickAwayListener, Grow, Icon, Popper } from "@material-ui/core";
import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import React, {
  CSSProperties,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { moneyMask } from "utils/utils";
import { ReactComponent as Balance } from "../../../../assets/icons/balance.svg";
import { ReactComponent as Limit } from "../../../../assets/icons/dailyLimit.svg";
import { ReactComponent as Card } from "../../../../assets/icons/nominal.svg";
import { ReactComponent as User } from "../../../../assets/icons/user.svg";
import meeLogo from "../../../../assets/images/logo-mee.png";
import meepLogo from "../../../../assets/images/m-white.png";
import OverlayPortal from "./components/OverlayPortal";
import styles from "./Toolbar.module.scss";
import { useTranslation } from "i18n/useTranslation";

export interface IToolbar {
  title: string;
  hideWelcome?: boolean;
  handleGoBack?: VoidFunction;
  hideCloseButton?: boolean;
}

const Toolbar: FC<IToolbar> = ({
  title,
  hideWelcome,
  handleGoBack,
  hideCloseButton,
}) => {
  const history = useHistory();
  const { kiosk } = useKiosk();
  const { resolution } = useScreenStore();
  const { cardData, isNominalData } = useCardReader();
  const { t } = useTranslation();

  const [showNominalData, setshowNominalData] = useState(false);
  const iconRef = useRef<HTMLDivElement | null>(null);

  const isNominalCatalog = useMemo(
    () => kiosk?.paymentNominalCashless && cardData && isNominalData.current,
    [cardData, isNominalData, kiosk]
  );

  const goBack = useCallback(() => {
    const { pathname } = history.location;

    if (pathname === "/") {
      return;
    }

    if (pathname.startsWith("/nominalCashless")) {
      history.replace("/");
      return;
    }

    if (pathname.startsWith("/catalog") && isNominalCatalog) {
      history.replace("/");
      return;
    }

    history.goBack();
  }, [history, isNominalCatalog]);

  const openNominalData = () => {
    setshowNominalData(true);
  };

  const closeNominalData = () => {
    setshowNominalData(false);
  };

  const onImgError = useCallback(
    (ev: React.ChangeEvent<HTMLImageElement>) => {
      ev.target.src = (kiosk?.isMee || kiosk?.isMeeConsulting) ? meeLogo : meepLogo;
    },
    [kiosk]
  );

  const logo = useMemo(
    () =>
      kiosk?.logoId
        ? `${process.env.REACT_APP_URL_IMAGEM}logo/${kiosk.logoId}.png`
        : kiosk?.isMee || kiosk?.isMeeConsulting
          ? meeLogo
          : meepLogo,
    [kiosk]
  );

  return (
    <div
      id={styles.Toolbar}
      style={
        {
          "--dynamic-color": kiosk?.themeColor ?? kiosk?.primaryColor,
        } as CSSProperties
      }
    >
      {hideCloseButton ? (
        <span></span>
      ) : (
        <Icon
          fontSize="large"
          className={styles.goBack}
          onClick={handleGoBack ?? goBack}
        >
          chevron_left
        </Icon>
      )}
      <img
        src={logo}
        alt="Logo"
        onErrorCapture={onImgError}
        style={{
          height: resolution === ResolutionType.FULL_HD ? 150 : 140,
          width: resolution === ResolutionType.FULL_HD ? 150 : 140,
        }}
      />
      <div
        style={{
          flex: hideCloseButton ? 2 : 5,
        }}
      >
        {hideWelcome ? <p></p> : <p>{t("toolbar.welcome")}</p>}
        {!isNominalCatalog && <p
          style={{ fontSize: resolution === ResolutionType.FULL_HD ? 48 : 40 }}
        >
          {title}
        </p>}

        {isNominalCatalog && (
          <div
            className={styles.nominalContainer}
            onClick={openNominalData}
            ref={iconRef}
          >
            <User color="#555555" />
            <b>{cardData?.holderName ? cardData.holderName.split(" ")[0] : ""}</b>
            <Popper
              open={showNominalData}
              anchorEl={iconRef.current}
              placement="bottom-end"
              transition
              style={{ zIndex: 10000 }}
            >
              {({ TransitionProps }) => (
                <Grow
                  {...TransitionProps}
                  timeout={600}
                  style={{ transformOrigin: "top right" }}
                >
                  <div className={styles.nominalModalContainer}>
                    <ClickAwayListener onClickAway={closeNominalData}>
                      <div>
                        <p>{cardData?.holderName ? cardData.holderName : ""}</p>
                        <div>
                          <Card />
                          <div>
                            <b>{t("toolbar.card_label")} </b>
                            <p>{cardData?.tag ? cardData.tag : "-"}</p>
                          </div>
                        </div>
                        <div>
                          <Limit />
                          <div>
                            <b>{t("toolbar.daily_limit_label")} </b>
                            <p>
                              {cardData?.dailyLimit
                                ? moneyMask(cardData.dailyLimit)
                                : t("toolbar.daily_limit_undefined")}
                            </p>
                          </div>
                        </div>
                        <div>
                          <Balance />
                          <div>
                            <b>{t("toolbar.balance_label")} </b>
                            <p>
                              {cardData?.balance
                                ? moneyMask(cardData.balance)
                                : "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </ClickAwayListener>
                  </div>
                </Grow>
              )}
            </Popper>
          </div>
        )}

      </div>



      {showNominalData && <OverlayPortal onClick={closeNominalData} />}
    </div>
  );
};

export default Toolbar;
